/**
 * Sentry Instrumentation
 * 
 * This file configures Sentry for error monitoring, performance tracking,
 * session replay, and logging. It must be imported BEFORE any other imports
 * in the application entry point (main.tsx).
 * 
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from "@sentry/react";

// Sentry DSN from environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION || 'unknown';

// Only initialize Sentry if DSN is provided
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment and release tracking
    environment: ENVIRONMENT,
    release: RELEASE,
    
    // Session Replay sampling rates
    // Capture 10% of all sessions
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0,
    // Capture 100% of sessions with errors
    replaysOnErrorSampleRate: ENVIRONMENT === 'production' ? 1.0 : 0.1,
    
    // Performance Monitoring
    integrations: [
      // Browser tracing for automatic performance instrumentation
      Sentry.browserTracingIntegration({
        // Trace navigation and user interactions
        enableInp: true,
      }),
      
      // Session Replay for video-like reproductions of user sessions
      Sentry.replayIntegration({
        // Mask all text and media content for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
      
      // Console logging integration - send console errors/warnings to Sentry
      Sentry.consoleLoggingIntegration({
        levels: ['error', 'warn'],
      }),
    ],
    
    // Tracing configuration
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
    // Lower in production to reduce volume (e.g., 0.1 = 10%)
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    
    // Capture user IP and request headers for better debugging
    // Note: Be mindful of GDPR/privacy requirements
    sendDefaultPii: true,
    
    // Enable automatic logging
    enableLogs: true,
    
    // Ignore common errors that don't need tracking
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors
      'Network request failed',
      'NetworkError',
      // User cancelled actions
      'AbortError',
      'The user aborted a request',
    ],
    
    // Filter out localhost/development transactions in production
    beforeSendTransaction(transaction) {
      // Don't send transactions from local development
      if (transaction.request?.url?.includes('localhost')) {
        return null;
      }
      return transaction;
    },
    
    // Add custom context to all events
    beforeSend(event, hint) {
      // Add custom tags
      event.tags = {
        ...event.tags,
        platform: 'frontend',
        framework: 'react',
      };
      
      // Log error to console in development
      if (ENVIRONMENT === 'development') {
        console.error('Sentry Error:', hint.originalException || hint.syntheticException);
      }
      
      return event;
    },
  });
  
  // Set user context (will be populated after authentication)
  // This can be updated later via Sentry.setUser() after login
  Sentry.setContext("app", {
    name: "TurboClaim Frontend",
    version: RELEASE,
    environment: ENVIRONMENT,
  });
  
  console.info('✅ Sentry initialized', {
    environment: ENVIRONMENT,
    release: RELEASE,
    dsn: SENTRY_DSN.split('@')[1], // Log only the domain, not the key
  });
} else {
  console.warn('⚠️ Sentry DSN not configured - error monitoring disabled');
}

// Export Sentry for use throughout the application
export default Sentry;
